<script>
  import { createEventDispatcher } from 'svelte';
  import { goto } from '$app/navigation';

  import AppInviteAjax from '@ajax/app_invite';
  import UserAjax from '@ajax/user';
  import Fullstory from '@utils/fullstory';
  import Log from '@utils/log';
  import Auth from '@utils/auth';

  import Modal from '@components/Modal.svelte';

  import CreateOrLoginStep from './create_account/CreateOrLoginStep.svelte';
  import UsernameStep from './create_account/UsernameStep.svelte';
  import InviteCodeStep from './create_account/InviteCodeStep.svelte';
  import CongratsStep from './create_account/CongratsStep.svelte';
  import NoInviteCongratsStep from './create_account/NoInviteCongratsStep.svelte';
  import PhotoUploadStep from './create_account/PhotoUploadStep.svelte';
  import UserBioStep from './create_account/UserBioStep.svelte';
  import Track from '@utils/track';

  import { getCurrentUserStore } from '@stores/current_user';

  const dispatch = createEventDispatcher();

  export let showModal = false;
  export let mobile = false;
  export let overlayClass;
  export let allowClose = true;
  export let successCallback = function () {};
  export let afterLoginCallback = function () {};
  export let afterUserCreateCallback = function () {};
  export let createOrLoginEmailValidationFn = async (_emailValue) => {};
  export let saveButtonFlow = false;
  export let inviteCode = null;
  export let showInvitedUserBanner = true;
  export let showActionLink = true;
  export let fetchInviteCodeAutomaticallyUserId = null;

  export let prefilledName = null;
  export let prefilledEmail = null;
  export let prefilledInstagramHandle = null;
  export let prefilledTiktokHandle = null;
  export let prefilledBio = null;
  export let prefilledUsername = null;
  export let prefilledProfileImageUrl = null;
  export let eventDetails = null;

  let inviteUser = null;
  let currentUser = getCurrentUserStore('default');

  export let beforeDataSend = (data) => {
    return data;
  };

  let pendingUserData = {};
  let showUsernameStep = false;
  let showInviteCodeStep = false;
  let showCongratsStep = false;
  let showNoInviteCongratsStep = false;
  let showPhotoUploadStep = false;
  let showUserBioStep = false;
  let createdUser;

  $: showDefaultStep =
    !showUsernameStep &&
    !showInviteCodeStep &&
    !showCongratsStep &&
    !showNoInviteCongratsStep &&
    !showPhotoUploadStep &&
    !showUserBioStep;

  $: showCloseButton = allowClose && !showPhotoUploadStep && !showUserBioStep;

  let close = function () {
    if (showCongratsStep) {
      if (location.href && location.href.includes('/discover')) {
        location.reload();
      } else {
        goto(`/discover`);
      }
    } else {
      showModal = false;
      showInviteCodeStep = false;
      showCongratsStep = false;
      showPhotoUploadStep = false;
      showNoInviteCongratsStep = false;
      showUserBioStep = false;
      createdUser = undefined;
      dispatch('close');
    }
  };

  let trackWaitlistMessageView = function () {
    Track.waitlistMessageView({
      pendingUserEmail: pendingUserData && pendingUserData.email,
      pageUrl: window.location.href,
    });
  };

  let goToInviteCodeStep = function () {
    showInviteCodeStep = true;
    showUsernameStep = false;
    showCongratsStep = false;
    showPhotoUploadStep = false;
    showNoInviteCongratsStep = false;
    showUserBioStep = false;

    trackWaitlistMessageView();
  };

  let goToUsernameStep = function () {
    showCongratsStep = false;
    showInviteCodeStep = false;
    showUsernameStep = true;
    showPhotoUploadStep = false;
    showNoInviteCongratsStep = false;
    showUserBioStep = false;
  };

  let goToCongratsStep = function () {
    showCongratsStep = true;
    showInviteCodeStep = false;
    showUsernameStep = false;
    showPhotoUploadStep = false;
    showNoInviteCongratsStep = false;
    showUserBioStep = false;
  };

  let goToNoInvitesCongratsStep = function () {
    showCongratsStep = false;
    showInviteCodeStep = false;
    showUsernameStep = false;
    showPhotoUploadStep = false;
    showNoInviteCongratsStep = true;
    showUserBioStep = false;
  };

  let goToPhotoUploadStep = function () {
    showCongratsStep = false;
    showInviteCodeStep = false;
    showUsernameStep = false;
    showPhotoUploadStep = true;
    showNoInviteCongratsStep = false;
    showUserBioStep = false;
  };

  let goToUserBioStep = function () {
    showCongratsStep = false;
    showInviteCodeStep = false;
    showUsernameStep = false;
    showPhotoUploadStep = false;
    showNoInviteCongratsStep = false;
    showUserBioStep = true;
  };

  let generatePossibleGroupIdeas = async function () {
    let resp = await UserAjax.generatePossibleGroupIdeas();

    if (resp && resp.processing) {
      currentUser.set({ ...currentUser, has_group_possible_ideas: resp.processing });
    }
  };

  let triggerMetaPixelTrackRegistration = () => {
    if (typeof window !== 'undefined' && window.fbq !== undefined) {
      window.fbq('track', 'CompleteRegistration');
    }
  };

  let convertPendingUserToActualUser = async function (userCodeVar, inviteCodeVar, displayName) {
    let resp = await AppInviteAjax.convertPendingUserToUser(userCodeVar, {
      invite_code: inviteCodeVar,
    });

    if (currentUser) {
      await Auth.signout(currentUser, { redirectToRoot: false });
    }

    Log.info(
      `[PENDING USER CONVERTED TO REAL USER] ${displayName} (${resp.invited_url}) used invite code by: ${resp.inviter_name} {${inviteCodeVar}} (${resp.inviter_url})`
    );

    if (!inviteUser) {
      inviteUser = {
        name: resp.inviter_name,
        avatar_url: resp.inviter_avatar_url,
        first_name: resp.inviter_first_name,
      };
    }

    let user = await Auth.load(currentUser, resp.token);

    triggerMetaPixelTrackRegistration();

    if (user && window.APP_ENV === 'production') {
      Fullstory.recordFS(user);
    }

    dispatch('user-created', { user });

    return user;
  };

  let fetchInviteUser = async function (inviteCodeToFetch) {
    let resp = await AppInviteAjax.get(inviteCodeToFetch);
    inviteUser = {
      name: resp.user.name,
      avatar_url: resp.user.avatar_url,
      id: resp.user.id,
      first_name: resp.user.first_name,
    };
  };

  let fetchInviteCodeAutomatically = async function () {
    let resp = await AppInviteAjax.skipInviteCode(fetchInviteCodeAutomaticallyUserId);

    if (resp && resp.code && !inviteCode) {
      inviteCode = resp.code;
      fetchInviteUser(inviteCode);
    }
  };

  $: getContainerClass = function () {
    if (showInviteCodeStep || showNoInviteCongratsStep) {
      return mobile ? 'mobile-invite-flow-modal__container' : 'desktop-invite-flow-modal__container';
    } else if (showUsernameStep) {
      return mobile ? 'modal-username-step-outer mobile-modal__container' : 'desktop-invite-flow-modal__container';
    } else if (showUserBioStep) {
      return mobile
        ? 'mobile-invite-flow-modal__container user-bio-step-outer'
        : 'desktop-invite-flow-modal__container user-bio-step-outer';
    } else if (showCongratsStep) {
      return mobile
        ? 'mobile-invite-flow-modal__container congrats-step'
        : 'desktop-invite-flow-modal__container congrats-step';
    } else if (showPhotoUploadStep) {
      return mobile ? 'mobile-invite-flow-photos-modal__container' : 'desktop-invite-flow-photos-modal__container';
    } else {
      return mobile ? 'mobile-modal__container' : 'desktop-modal__container';
    }
  };

  if (inviteCode) {
    fetchInviteUser(inviteCode);
  }

  if (eventDetails) {
    Track.signupModalOpened({ ...eventDetails, page_url: location.pathname + location.search });
  }

  if (fetchInviteCodeAutomaticallyUserId && !inviteCode) {
    fetchInviteCodeAutomatically();
  }
</script>

<style style="text/scss" lang="scss">
  @import '../../vendor/design-system.scss';

  * :global(.desktop-modal__container) {
    border-radius: 16px !important;
    min-width: 1100px !important;
    height: calc(100vh - 80px) !important;
    overflow: hidden !important;
    margin-top: 0px !important;
    margin-bottom: 0px !important;
    max-height: 860px !important;
  }

  * :global(.desktop-invite-flow-modal__container) {
    border-radius: 16px !important;
    width: 100% !important;
    max-width: 780px !important;
    height: 648px !important;
    max-height: 648px !important;
    overflow: hidden !important;
    margin-top: 0px !important;
    margin-bottom: 0px !important;
  }

  * :global(.user-bio-step-outer) {
    background: linear-gradient(175deg, #fff, #fff 25%, #eae4ff) !important;
  }
  * :global(.desktop-invite-flow-modal__container.user-bio-step-outer) {
    height: 684px !important;
    max-height: 684px !important;
  }

  * :global(.desktop-invite-flow-modal__container.congrats-step) {
    height: 684px !important;
    max-height: 684px !important;
    background: linear-gradient(343.65deg, #eae4ff -0.74%, #ffffff 72.21%),
      linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
  }

  * :global(.mobile-invite-flow-modal__container.congrats-step) {
    background: linear-gradient(343.65deg, #eae4ff -0.74%, #ffffff 72.21%),
      linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
  }

  * :global(.mobile-invite-flow-photos-modal__container) {
    border-radius: 16px !important;
    width: 604px !important;
    max-width: 604px !important;
    max-width: 780px !important;
    height: 590px !important;
    max-height: 590px !important;
    overflow: hidden !important;
    margin-top: 0px !important;
    margin-bottom: 0px !important;
  }

  * :global(.desktop-invite-flow-photos-modal__container) {
    border-radius: 16px !important;
    width: 604px !important;
    max-width: 604px !important;
    max-width: 780px !important;
    height: 654px !important;
    max-height: 654px !important;
    overflow: hidden !important;
    margin-top: 0px !important;
    margin-bottom: 0px !important;
  }

  * :global(.mobile-invite-flow-modal__container) {
    overflow-x: hidden !important;
    min-height: unset !important;
    height: unset !important;
    top: 70px !important;
    bottom: 10px !important;
    left: 10px !important;
    right: 10px !important;
  }

  * :global(.mobile-invite-flow-modal__container.user-bio-step-outer) {
    top: 20px !important;
  }

  * :global(.modal__container.mobile-modal__container.modal-username-step-outer) {
    margin-top: 20px !important;
  }

  * :global(.mobile-modal__container.modal-username-step-outer .modal__content) {
    margin-top: 10px;
  }

  * :global(.modal__close) {
    position: absolute;
    right: 20px;
    top: 15px;
  }
</style>

<div class="svelte-modal svelte-create-login-modal">
  <Modal
    mobile={mobile}
    on:close={close}
    show={showModal}
    showCloseButton={showCloseButton}
    fadeInDuration={0}
    allowClose={allowClose}
    overlayClass={overlayClass}
    closeOnOverlayClick={false}
    containerClass={getContainerClass()}
    style={getContainerClass().includes('mobile-invite-flow-modal__container')
      ? 'position: absolute !important; margin: 0px !important; padding: 0px !important; width: unset !important;'
      : ''}>
    <div slot="content" style="text-align: center;">
      <div style={showInviteCodeStep ? 'display: block' : 'display: none'}>
        <InviteCodeStep
          mobile={mobile}
          successCallback={successCallback}
          pendingUserData={pendingUserData}
          convertPendingUserToActualUser={convertPendingUserToActualUser}
          closeModal={close}
          on:finished={(event) => {
            if (event.detail.user) {
              createdUser = event.detail.user;
              goToUsernameStep();
            } else {
              close();
            }
          }}
          on:go-to-no-invites-contrats-step={goToNoInvitesCongratsStep} />
      </div>
      <div style={showUsernameStep ? 'display: block' : 'display: none'}>
        <UsernameStep
          showInvitedUserBanner={showInvitedUserBanner}
          mobile={mobile}
          inviteUser={inviteUser}
          successCallback={successCallback}
          saveButtonFlow={saveButtonFlow}
          prefilledUsername={prefilledUsername}
          closeModal={close}
          on:finished={(event) => {
            createdUser = event.detail.user;
            if (saveButtonFlow) {
              successCallback(createdUser);
            } else {
              goToPhotoUploadStep();
            }
          }}
          } />
      </div>
      <div style={showCongratsStep ? 'display: block' : 'display: none'}>
        <CongratsStep
          closeModal={close}
          mobile={mobile}
          pendingUserData={pendingUserData}
          createdUser={createdUser}
          on:finished={(_event) => {
            successCallback(createdUser);
            close();
          }} />
      </div>
      <div style={showNoInviteCongratsStep ? 'display: block' : 'display: none'}>
        <NoInviteCongratsStep mobile={mobile} pendingUserData={pendingUserData} />
      </div>
      <div style={showPhotoUploadStep ? 'display: block' : 'display: none'}>
        <PhotoUploadStep
          mobile={mobile}
          createdUser={createdUser}
          prefilledProfileImageUrl={prefilledProfileImageUrl}
          on:finished={() => {
            goToUserBioStep();
          }} />
      </div>
      <div style={showUserBioStep ? 'display: block' : 'display: none'}>
        <UserBioStep
          mobile={mobile}
          createdUser={createdUser}
          prefilledInstagramHandle={prefilledInstagramHandle}
          prefilledTiktokHandle={prefilledTiktokHandle}
          prefilledBio={prefilledBio}
          on:finished={() => {
            setTimeout(generatePossibleGroupIdeas, 1000);
            goToCongratsStep();
          }} />
      </div>
      <div style={showDefaultStep ? 'display: block' : 'display: none'}>
        <CreateOrLoginStep
          {...$$props}
          closeModal={close}
          mobile={mobile}
          successCallback={successCallback}
          afterLoginCallback={afterLoginCallback}
          beforeDataSend={beforeDataSend}
          saveButtonFlow={saveButtonFlow}
          emailValidationFn={createOrLoginEmailValidationFn}
          showActionLink={showActionLink}
          prefilledName={prefilledName}
          prefilledEmail={prefilledEmail}
          on:go-to-username-step={(event) => {
            pendingUserData = event.detail;

            if (inviteCode) {
              convertPendingUserToActualUser(pendingUserData.code, inviteCode, pendingUserData.name).then((user) => {
                createdUser = user;
                afterUserCreateCallback(createdUser);
                goToUsernameStep();
              });
            } else if (saveButtonFlow) {
              goToUsernameStep();
            } else {
              goToInviteCodeStep();
            }
          }} />
      </div>
    </div>
  </Modal>
</div>
